import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
	SocialBulkDecisionRequest,
	SocialClientSummary,
	SocialComparisonResponse,
	SocialDecision,
	SocialDecisionAction,
	SocialExample,
	SocialExcludedProfile,
	SocialExcludeFromDecisionRequest,
	SocialKeyword,
	SocialKeywordHealth,
	SocialPageResponse,
	SocialRejectReason,
	SocialReviewFilter,
	SocialSet,
	SocialSetSummary,
	SocialSettings,
} from '../interfaces/social.interfaces';

// Hand-written HTTP calls (not the generated api-clipping SDK) for the "Socijalni mrezi"
// endpoints - see social.interfaces.ts for why. Auth header is added by the app's existing
// HTTP interceptor, same as every other service here.
@Injectable({ providedIn: 'root' })
export class SocialApiService {
	private readonly base = `${environment.apiClippingUrl}/v1/management/social`;

	constructor(private http: HttpClient) {}

	getReview(filter: SocialReviewFilter, page: number, size: number): Observable<SocialPageResponse<SocialDecision>> {
		return this.http.post<SocialPageResponse<SocialDecision>>(`${this.base}/review`, filter, { params: { page, size } });
	}

	decide(decisionId: string, action: SocialDecisionAction): Observable<SocialDecision> {
		return this.http.post<SocialDecision>(`${this.base}/review/${decisionId}/decide`, action);
	}

	// "Prifati gi site nad X%" (Dopolnuvanje 2.1).
	countAwaitingReviewAboveThreshold(clientId: string | null, setId: string | null, threshold: number): Observable<number> {
		const params: Record<string, string | number> = { threshold };
		if (clientId) {
			params['clientId'] = clientId;
		}
		if (setId) {
			params['setId'] = setId;
		}
		return this.http.get<number>(`${this.base}/review/count-above-threshold`, { params });
	}

	acceptAllAboveThreshold(clientId: string | null, setId: string | null, threshold: number): Observable<number> {
		const params: Record<string, string | number> = { threshold };
		if (clientId) {
			params['clientId'] = clientId;
		}
		if (setId) {
			params['setId'] = setId;
		}
		return this.http.post<number>(`${this.base}/review/accept-above-threshold`, {}, { params });
	}

	// "Prifati izbrani" / "Otfrli izbrani" (docs/SOCIJALNI-FAZA1-EKRAN.md section 3).
	bulkDecide(request: SocialBulkDecisionRequest): Observable<number> {
		return this.http.post<number>(`${this.base}/review/bulk-decide`, request);
	}

	// Point 7: optional reject-reason filter, on top of the existing client/set ones.
	getTrash(clientId: string | null, setId: string | null, reason: SocialRejectReason | null, page: number, size: number): Observable<SocialPageResponse<SocialDecision>> {
		const params: Record<string, string | number> = { page, size };
		if (clientId) {
			params['clientId'] = clientId;
		}
		if (setId) {
			params['setId'] = setId;
		}
		if (reason) {
			params['reason'] = reason;
		}
		return this.http.get<SocialPageResponse<SocialDecision>>(`${this.base}/trash`, { params });
	}

	// Krug 2 point 2b: optional "why", saved as the restored example's reason (see social-korpa).
	restore(decisionId: string, why?: string): Observable<SocialDecision> {
		return this.http.post<SocialDecision>(`${this.base}/trash/${decisionId}/restore`, { why });
	}

	// "Isprazni" (section 4), now also honoring the reason filter (point 7).
	countTrash(clientId?: string | null, reason?: SocialRejectReason | null): Observable<number> {
		const params: Record<string, string> = {};
		if (clientId) {
			params['clientId'] = clientId;
		}
		if (reason) {
			params['reason'] = reason;
		}
		return this.http.get<number>(`${this.base}/trash/count`, { params });
	}

	clearTrash(clientId?: string | null, reason?: SocialRejectReason | null): Observable<number> {
		const params: Record<string, string> = {};
		if (clientId) {
			params['clientId'] = clientId;
		}
		if (reason) {
			params['reason'] = reason;
		}
		return this.http.delete<number>(`${this.base}/trash`, { params });
	}

	// Point 7: the count badge per reason in the Korpa filter dropdown.
	getTrashReasonCounts(clientId?: string | null): Observable<Record<SocialRejectReason, number>> {
		const params: Record<string, string> = {};
		if (clientId) {
			params['clientId'] = clientId;
		}
		return this.http.get<Record<SocialRejectReason, number>>(`${this.base}/trash/reason-counts`, { params });
	}

	// "Dosieja": client -> sets. Left-hand client list - only clients with keywords (section 2).
	listClientsWithKeywords(): Observable<SocialClientSummary[]> {
		return this.http.get<SocialClientSummary[]>(`${this.base}/clients`);
	}

	deleteAllForClient(clientId: string): Observable<void> {
		return this.http.delete<void>(`${this.base}/clients/${clientId}/sets`);
	}

	listSetsForClient(clientId: string): Observable<SocialSetSummary[]> {
		return this.http.get<SocialSetSummary[]>(`${this.base}/clients/${clientId}/sets`);
	}

	createSetForClient(clientId: string, name: string, clientName: string): Observable<SocialSet> {
		return this.http.post<SocialSet>(`${this.base}/clients/${clientId}/sets`, { name, clientName });
	}

	// "Без клиент" bucket.
	listUnassignedSets(): Observable<SocialSetSummary[]> {
		return this.http.get<SocialSetSummary[]>(`${this.base}/sets/unassigned`);
	}

	createUnassignedSet(name: string): Observable<SocialSet> {
		return this.http.post<SocialSet>(`${this.base}/sets/unassigned`, { name });
	}

	getSet(setId: string): Observable<SocialSet> {
		return this.http.get<SocialSet>(`${this.base}/sets/${setId}`);
	}

	updateSet(setId: string, name: string, rules: string): Observable<SocialSet> {
		return this.http.put<SocialSet>(`${this.base}/sets/${setId}`, { name, rules });
	}

	renameSet(setId: string, name: string): Observable<SocialSet> {
		return this.http.put<SocialSet>(`${this.base}/sets/${setId}/name`, { name });
	}

	updateSetTags(setId: string, tagIds: string[]): Observable<SocialSet> {
		return this.http.put<SocialSet>(`${this.base}/sets/${setId}/tags`, { tagIds });
	}

	assignSetClient(setId: string, clientId: string, clientName: string): Observable<SocialSet> {
		return this.http.put<SocialSet>(`${this.base}/sets/${setId}/client`, { clientId, clientName });
	}

	deleteSet(setId: string): Observable<void> {
		return this.http.delete<void>(`${this.base}/sets/${setId}`);
	}

	addKeywords(setId: string, texts: string[]): Observable<{ id: string; text: string }[]> {
		return this.http.post<{ id: string; text: string }[]>(`${this.base}/sets/${setId}/keywords`, { texts });
	}

	deleteKeyword(setId: string, keywordId: string): Observable<void> {
		return this.http.delete<void>(`${this.base}/sets/${setId}/keywords/${keywordId}`);
	}

	// CLIPPING-610 point 5: rename in place instead of delete+re-add.
	renameKeyword(setId: string, keywordId: string, text: string): Observable<SocialKeyword> {
		return this.http.put<SocialKeyword>(`${this.base}/sets/${setId}/keywords/${keywordId}`, { text });
	}

	// "+ drug tag" po zbor (section 2).
	updateKeywordTags(setId: string, keywordId: string, tagIds: string[]): Observable<SocialKeyword> {
		return this.http.put<SocialKeyword>(`${this.base}/sets/${setId}/keywords/${keywordId}/tags`, { tagIds });
	}

	getKeywordHealth(setId: string): Observable<SocialKeywordHealth[]> {
		return this.http.get<SocialKeywordHealth[]>(`${this.base}/sets/${setId}/keyword-health`);
	}

	addExample(setId: string, snippet: string, decision: boolean, reason?: string): Observable<SocialExample> {
		return this.http.post<SocialExample>(`${this.base}/sets/${setId}/examples`, { snippet, decision, reason });
	}

	deleteExample(setId: string, exampleId: string): Observable<void> {
		return this.http.delete<void>(`${this.base}/sets/${setId}/examples/${exampleId}`);
	}

	getComparison(date: string, clientId?: string | null): Observable<SocialComparisonResponse> {
		const params: Record<string, string> = { date };
		if (clientId) {
			params['clientId'] = clientId;
		}
		return this.http.get<SocialComparisonResponse>(`${this.base}/comparison`, { params });
	}

	// Point 5: "Залепи линкови од продукција".
	compareWithPastedLinks(date: string, clientId: string | null, links: string[]): Observable<SocialComparisonResponse> {
		return this.http.post<SocialComparisonResponse>(`${this.base}/comparison/pasted-links`, { date, clientId, links });
	}

	getSettings(): Observable<SocialSettings> {
		return this.http.get<SocialSettings>(`${this.base}/settings`);
	}

	updateSettings(settings: SocialSettings): Observable<SocialSettings> {
		return this.http.put<SocialSettings>(`${this.base}/settings`, settings);
	}

	// "Isklucheni profili" - per client.
	listExcludedProfilesForClient(clientId: string): Observable<SocialExcludedProfile[]> {
		return this.http.get<SocialExcludedProfile[]>(`${this.base}/clients/${clientId}/excluded-profiles`);
	}

	addExcludedProfileForClient(clientId: string, link: string, label?: string): Observable<SocialExcludedProfile> {
		return this.http.post<SocialExcludedProfile>(`${this.base}/clients/${clientId}/excluded-profiles`, { link, label });
	}

	deleteExcludedProfileForClient(clientId: string, id: string): Observable<void> {
		return this.http.delete<void>(`${this.base}/clients/${clientId}/excluded-profiles/${id}`);
	}

	// Point 5: label-only edit.
	updateExcludedProfileForClient(clientId: string, id: string, label: string): Observable<SocialExcludedProfile> {
		return this.http.put<SocialExcludedProfile>(`${this.base}/clients/${clientId}/excluded-profiles/${id}`, { label });
	}

	listGlobalExcludedProfiles(): Observable<SocialExcludedProfile[]> {
		return this.http.get<SocialExcludedProfile[]>(`${this.base}/excluded-profiles/global`);
	}

	addGlobalExcludedProfile(link: string, label?: string): Observable<SocialExcludedProfile> {
		return this.http.post<SocialExcludedProfile>(`${this.base}/excluded-profiles/global`, { link, label });
	}

	deleteGlobalExcludedProfile(id: string): Observable<void> {
		return this.http.delete<void>(`${this.base}/excluded-profiles/global/${id}`);
	}

	// Point 5: label-only edit.
	updateGlobalExcludedProfile(id: string, label: string): Observable<SocialExcludedProfile> {
		return this.http.put<SocialExcludedProfile>(`${this.base}/excluded-profiles/global/${id}`, { label });
	}

	excludeProfileFromDecision(decisionId: string, request: SocialExcludeFromDecisionRequest): Observable<SocialExcludedProfile> {
		return this.http.post<SocialExcludedProfile>(`${this.base}/decisions/${decisionId}/exclude-profile`, request);
	}
}
